import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { 
  UserGroupIcon, 
  PlusIcon,
  AcademicCapIcon,
  EyeIcon,
  PencilIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { getCourses } from "../../../../api/hse/competency";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";
import { toast } from "react-hot-toast";

/**
 * HSE/KS Competency Courses - Training courses list
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyCourses = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
    statuses: [],
  });
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    fetchCourses();
  }, [filters]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const apiFilters = {
        search: filters.query,
        status: filters.statuses.length > 0 ? filters.statuses[0] : undefined,
      };
      
      const response = await getCourses(apiFilters);
      setCourses(response.courses || []);
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError(true);
      toast.error(t("Kunne ikke laste kurs"));
    } finally {
      setLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters({
      query: newFilters.query || "",
      statuses: newFilters.statuses || [],
    });
  };

  // Status options for ModuleFilter
  const statusOptions = [
    { value: "upcoming", label: t("Kommende"), class: "bg-yellow-100 text-yellow-800" },
    { value: "ongoing", label: t("Pågående"), class: "bg-blue-100 text-blue-800" },
    { value: "completed", label: t("Fullført"), class: "bg-green-100 text-green-800" },
    { value: "cancelled", label: t("Avlyst"), class: "bg-red-100 text-red-800" },
  ];

  const headerActions = canManage ? [
    {
      label: t("Nytt kurs"),
      icon: PlusIcon,
      path: "/dashboard/hse-ks/competency/courses/new",
      hidden: false,
      menu: false,
    },
  ] : [];

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste kurs")]} />;
  }

  return (
    <HsePageChrome
      title={t("Kurs")}
      subtitle={t("Oversikt over kurs og opplæring")}
      actions={headerActions}
      backButton={{
        label: t("Tilbake til kompetanse"),
        action: () => navigate("/dashboard/hse-ks/competency"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <CompetencySubnav />
          {/* ModuleFilter */}
          <ModuleFilter
            onFiltersChange={handleFiltersChange}
            initialFilters={filters}
            searchPlaceholder={t("Søk i kurs...")}
            acceptedStatuses={statusOptions.map(s => s.value)}
          />

          {/* Courses List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Kurs")} ({courses.length})
              </h3>
              
              {courses.length === 0 ? (
                <ResourceEmpty message={t("Ingen kurs funnet")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Kurs")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Dato")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Deltakere")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Status")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {courses.map((course) => (
                        <tr
                          key={course.id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => navigate(`/dashboard/hse-ks/competency/courses/${course.id}`)}
                        >
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div>
                                <div className="font-medium text-gray-900">{course.name}</div>
                                {course.description && (
                                  <div className="text-gray-500 text-xs mt-1 line-clamp-2">
                                    {course.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {course.start_date ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(course.start_date).toLocaleDateString("nb-NO")}
                                {course.end_date && ` - ${new Date(course.end_date).toLocaleDateString("nb-NO")}`}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <UserGroupIcon className="h-4 w-4 mr-1" />
                              {course.participants_count || 0} / {course.max_participants || "-"}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            {course.status === "completed" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                {t("Fullført")}
                              </span>
                            ) : course.status === "ongoing" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                {t("Pågående")}
                              </span>
                            ) : course.status === "upcoming" ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                {t("Kommende")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {t("Avlyst")}
                              </span>
                            )}
                          </td>
                          <td
                            className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="flex items-center justify-end space-x-2">
                              <Link
                                to={`/dashboard/hse-ks/competency/courses/${course.id}`}
                                className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                                title={t("Vis")}
                              >
                                <EyeIcon className="h-4 w-4 mr-1" />
                                {t("Vis")}
                              </Link>
                              {canManage && (
                                <Link
                                  to={`/dashboard/hse-ks/competency/courses/${course.id}/edit`}
                                  className="text-gray-600 hover:text-gray-900 inline-flex items-center"
                                  title={t("Rediger")}
                                >
                                  <PencilIcon className="h-4 w-4 mr-1" />
                                  {t("Rediger")}
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

